/**
 * Theme switching ships dormant.
 *
 * The DS dark layer and the provider are wired, but production is pinned to
 * light and the Appearance control is hidden until the surface conversion is
 * far enough along to look finished. Roughly 560 files still paint fixed
 * colors (`bg-white`, `bg-nf-ds-gray-50`), so a dark theme today renders as a
 * half-converted page rather than a dark one.
 *
 * Opt in per environment with:
 *
 *   NEXT_PUBLIC_ENABLE_THEME_SWITCHER="true"
 *
 * Off by default, matching NEXT_PUBLIC_ENABLE_REUSE_TARGET. This is read at
 * build time, not runtime — NEXT_PUBLIC_* values are inlined by Next, so
 * flipping it needs a rebuild, not just a restart.
 *
 * Both consumers must agree, which is why this lives in one place:
 *   - `components/Providers.tsx` pins `forcedTheme="light"` when disabled
 *   - the Appearance card on `/c/<client>/settings/general` renders only when
 *     enabled — a visible control that could not change anything is worse than
 *     no control
 *
 * THERE IS NOW A SECOND WAY OUT OF THE PIN, and it is a deliberate widening of
 * this policy rather than an application of it. `Providers` also releases the
 * pin for anyone on `DS_PREVIEW_EMAILS` (see `lib/dsPreview.ts`), on every
 * route rather than only the prototypes.
 *
 * The property this flag protects is that nobody unprepared lands on a
 * half-converted page. This switch was the only tool for that when it was
 * written — environment-wide and build-time, so the choice was all users or
 * none. A server-resolved per-user allowlist did not exist yet. It does now,
 * and it holds the same property by naming the people who asked to see the
 * unfinished work instead of naming the routes where unfinished work lives.
 *
 * What that costs: dark mode is no longer strictly route-scoped, so an
 * allowlisted person sees the ~560 files that still paint fixed colours
 * wherever they go. That is the point for them and would be a bug for anyone
 * else, which is why the list is the gate.
 *
 * THE THIRD WAY OUT IS BY ROUTE, and it is the first one that names where
 * finished work lives rather than who may see unfinished work. The creator
 * surfaces (`/one`, `/join`) wear the candidate design system outright and
 * have no fixed-colour debt: their dark ground is complete, and the portal's
 * Settings sheet already offers Light, Dark and System. On those routes the
 * pin is released for everyone, so a creator's choice, or their OS, applies.
 * Everywhere else the pin holds exactly as before. `RouteThemeProvider` is
 * the one consumer; it reads the pathname and asks `isThemePinned`.
 */
export const THEME_SWITCHER_ENABLED =
  process.env.NEXT_PUBLIC_ENABLE_THEME_SWITCHER === "true";

/**
 * The routes whose layouts wear `ds-next` and are fully themed. Keep this in
 * step with the layouts that add that class (`app/one/layout.tsx`,
 * `app/join/layout.tsx`): a route listed here renders dark as finished, so it
 * must be.
 */
export const THEME_UNPINNED_ROUTES = ["/one", "/join"] as const;

/**
 * Whether production pins this path to light.
 *
 * A prefix match on a path segment, so `/one/videos` is the portal and
 * `/onesie` is not. A missing pathname (never the case in the App Router, but
 * the hook's type allows it) pins, since pinning is the safe side.
 */
export function isThemePinned(pathname: string | null | undefined): boolean {
  if (!pathname) return true;
  return !THEME_UNPINNED_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}
